import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  Link,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  Star as StarIcon,
  Place as PlaceIcon,
  AccessTime as AccessTimeIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import FeedLayout from '../../components/feed/FeedLayout';
import { getLocation, getLocationReviews, getLocationAdmins } from '../../services/feedApi';
import { useAuth } from '../../contexts/AuthContext';


const OverviewTab = ({ location }) => (
  <Grid container spacing={4}>
    {/* Location Details */}
    <Grid item xs={12} md={6}>
      <Card sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <PlaceIcon sx={{ color: '#3b5998' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Location Details
            </Typography>
          </Box>
          
          <Box sx={{ '& > div': { py: 1.5, borderBottom: '1px solid', borderColor: 'divider' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Street</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{location.address?.street}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">City</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {location.address?.city}, {location.address?.state} {location.address?.zip}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Country</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{location.address?.country}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Phone</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{location.phone}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Website</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{location.website}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem' }}
                  onClick={() => window.open(`https://${location.website}`, '_blank')}
                >
                  URL
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Opening Date</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{location.openingDate}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: '60%', textAlign: 'right' }}>
                {location.description}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Hours */}
    <Grid item xs={12} md={6}>
      <Card sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <AccessTimeIcon sx={{ color: '#3b5998' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Hours
            </Typography>
          </Box>
          
          <Box sx={{ '& > div': { py: 1.5, borderBottom: '1px solid', borderColor: 'divider' } }}>
            {Object.entries(location.hours || {}).map(([day, hours]) => (
              <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {day}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{hours}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Categories & Maps Link */}
    <Grid item xs={12}>
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Categories
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(location.categories || []).map((cat, idx) => (
                  <Chip key={idx} label={cat} size="small" sx={{ bgcolor: 'rgba(59, 89, 152, 0.1)' }} />
                ))}
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(location.mapsUrl, '_blank')}
              sx={{ color: '#3b5998', borderColor: '#3b5998' }}
            >
              View on Google Maps
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

const ReviewsTab = ({ reviews, location }) => (
  <Box>
    {/* Stats */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={4}>
        <Card sx={{ p: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
            {location.reviewCount || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">Total Reviews</Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Card sx={{ p: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
              {location.rating || 0}
            </Typography>
            <StarIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
          </Box>
          <Typography variant="body2" color="text.secondary">Average Rating</Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Card sx={{ p: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#10b981' }}>
            A
          </Typography>
          <Typography variant="body2" color="text.secondary">Review Velocity Grade</Typography>
        </Card>
      </Grid>
    </Grid>

    {/* Rating Distribution */}
    <Card sx={{ border: '1px solid', borderColor: 'divider', p: 3, mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Rating Distribution</Typography>
      {[5, 4, 3, 2, 1].map((star) => {
        const percentage = star === 5 ? 65 : star === 4 ? 20 : star === 3 ? 10 : star === 2 ? 3 : 2;
        return (
          <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 50 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{star}</Typography>
              <StarIcon sx={{ color: '#f59e0b', fontSize: 16, ml: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b', borderRadius: 4 },
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
              {percentage}%
            </Typography>
          </Box>
        );
      })}
    </Card>

    {/* Recent Reviews */}
    <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Reviews</Typography>
        {reviews.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No reviews available. Reviews will appear here when the API is connected.
          </Typography>
        ) : (
          <List disablePadding>
            {reviews.map((review, idx) => (
              <React.Fragment key={review.id}>
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#3b5998' }}>{review.avatar}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {review.author}
                        </Typography>
                        <Box sx={{ display: 'flex' }}>
                          {[...Array(5)].map((_, i) => (
                            <StarIcon
                              key={i}
                              sx={{ fontSize: 16, color: i < review.rating ? '#f59e0b' : '#e5e7eb' }}
                            />
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                    secondary={review.text}
                  />
                </ListItem>
                {idx < reviews.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  </Box>
);

const SocialTab = ({ social }) => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Social Profiles</Typography>
          {Object.keys(social || {}).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No social profiles available. Social links will appear here when the API is connected.
            </Typography>
          ) : (
            <Box sx={{ '& > div': { py: 2, borderBottom: '1px solid', borderColor: 'divider' } }}>
              {Object.entries(social || {}).map(([platform, handle]) => (
                <Box key={platform} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {platform}
                  </Typography>
                  <Link href={`https://${handle}`} target="_blank" sx={{ color: '#3b5998' }}>
                    {handle}
                  </Link>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12}>
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Recent Posts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No recent posts available. Posts from GBP will appear here when the API is connected.
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

const UserAccessTab = ({ admins }) => (
  <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Profile Access</Typography>
      {admins.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No access information available. User access data will appear here when the API is connected.
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(59, 89, 152, 0.04)' }}>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date Added</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id} hover>
                  <TableCell>{admin.name}</TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={admin.role}
                      size="small"
                      sx={{
                        bgcolor: admin.role === 'Owner' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 89, 152, 0.1)',
                        color: admin.role === 'Owner' ? '#10b981' : '#3b5998',
                      }}
                    />
                  </TableCell>
                  <TableCell>{new Date(admin.addedDate).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CardContent>
  </Card>
);

const FeedLocationDetail = () => {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [location, setLocation] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [locationData, reviewsData, adminsData] = await Promise.all([
          getLocation(id),
          getLocationReviews(id),
          getLocationAdmins(id),
        ]);
        setLocation(locationData);
        setReviews(reviewsData.reviews || []);
        setAdmins(adminsData.admins || []);
      } catch (err) {
        console.error('Error fetching location data:', err);
        const status = err.response?.status;
        
        if (status === 401 || status === 403) {
          return;
        }
        
        setError(
          status === 404
            ? 'Location not found or Feed API not available.'
            : `Failed to load location data: ${err.message}`
        );
        setLocation(null);
        setReviews([]);
        setAdmins([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const tabs = [
    { label: 'Overview', component: <OverviewTab location={location || {}} /> },
    { label: 'Reviews', component: <ReviewsTab reviews={reviews} location={location || {}} /> },
    { label: 'Social', component: <SocialTab social={location?.social || {}} /> },
  ];

  if (isAdmin) {
    tabs.push({ label: 'User Access', component: <UserAccessTab admins={admins} /> });
  }

  if (loading) {
    return (
      <FeedLayout>
        <Box sx={{ py: 4 }}>
          <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 4 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </FeedLayout>
    );
  }

  if (!location && error) {
    return (
      <FeedLayout
        title="Location"
        subtitle={`Store Code: ${id}`}
        breadcrumbs={[
          { label: 'Regions', path: '/feed/regions' },
          { label: 'Location' },
        ]}
      >
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Card sx={{ border: '1px solid', borderColor: 'divider', p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Unable to load location details. Please try again or contact support if the issue persists.
          </Typography>
        </Card>
      </FeedLayout>
    );
  }

  return (
    <FeedLayout
      title={location?.name || 'Location'}
      subtitle={`${location?.region || ''}, Store Code: ${location?.storeCode || id}`}
      breadcrumbs={[
        { label: 'Regions', path: '/feed/regions' },
        { label: location?.region || 'Region', path: `/feed/regions/${(location?.region || '').toLowerCase().replace(' ', '-')}` },
        { label: location?.name || 'Location' },
      ]}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
            '& .Mui-selected': { color: '#3b5998', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: '#3b5998' },
          }}
        >
          {tabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabs[activeTab]?.component}
    </FeedLayout>
  );
};

export default FeedLocationDetail;
